import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { type FileWithPath } from 'react-dropzone';

import { type Board } from '@air/api/types';

import { QueryParamNames } from '~/constants/search';
import { useGoToBoardPage } from '~/hooks/useGoToBoardPage';
import { useQueuePrivateUploads } from '~/hooks/useQueuePrivateUploads';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useUploadValidation } from '~/hooks/useUploadValidation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useCreateNewBoard } from '~/swr-hooks/boards/useCreateNewBoard';
import { pushWithExistingQuery } from '~/utils/PathUtils';
import { isMobileAgent } from '~/utils/PlatformHelpers';

const getSearchParamValue = (value: string | string[] | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  return Array.isArray(value) ? value[0] : value;
};

const decodeCanvasName = (canvasName: string) => {
  try {
    return decodeURIComponent(canvasName);
  } catch {
    return canvasName;
  }
};

const dataUriToFile = async (dataUri: string): Promise<FileWithPath | null> => {
  const normalizedDataUri = dataUri.replace(/\s/g, '+');
  const match = normalizedDataUri.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);

  if (!match) {
    return null;
  }

  const mimeType = match[1];

  try {
    const response = await fetch(normalizedDataUri);

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    const fileType = blob.type || mimeType;
    const subtype = fileType.split('/')[1] || 'png';
    const file: FileWithPath = new File([blob], `upload.${subtype}`, { type: fileType });

    return file;
  } catch {
    return null;
  }
};

export const useCreateBoardFromCanvasSearchParams = () => {
  const router = useRouter();
  const { currentWorkspace } = useCurrentWorkspace();
  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const { createNewBoard } = useCreateNewBoard();
  const { goToBoardPage } = useGoToBoardPage();
  const { queuePrivateUploads } = useQueuePrivateUploads();
  const { validateUpload } = useUploadValidation({ workspaceId: currentWorkspace?.id });
  const processedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!router.isReady || !generativeCanvasEnabled || !currentWorkspace?.id || !isMobileAgent) {
      return;
    }

    const canvasName = getSearchParamValue(router.query[QueryParamNames.canvasName]);
    const canvasUploadDataUri = getSearchParamValue(router.query[QueryParamNames.canvasUploadDataUri]);

    if (!canvasName || !canvasUploadDataUri) {
      processedKeyRef.current = null;
      return;
    }

    const processKey = `${canvasName}:${canvasUploadDataUri}`;

    if (processedKeyRef.current === processKey) {
      return;
    }

    processedKeyRef.current = processKey;

    const decodedCanvasName = decodeCanvasName(canvasName).trim();

    if (!decodedCanvasName) {
      return;
    }

    void (async () => {
      const file = await dataUriToFile(canvasUploadDataUri);

      if (!file) {
        return;
      }

      const validatedFiles = await validateUpload([file]);

      if (!validatedFiles) {
        return;
      }

      await pushWithExistingQuery({
        newQuery: {
          [QueryParamNames.canvasName]: undefined,
          [QueryParamNames.canvasUploadDataUri]: undefined,
        },
        method: 'replace',
      });

      let board: Board | undefined;

      try {
        board = await createNewBoard({
          board: { title: decodedCanvasName },
          trackLocation: 'file-uploader',
          shouldFollow: true,
        });
      } catch {
        // toast surfaced inside useCreateNewBoard
      }

      if (!board) {
        return;
      }

      await goToBoardPage({ board, method: 'replace' });

      const { files, emptyFiles, filesWithNoExtension } = validatedFiles;
      const topLevelFilesToUpload = files.map((f) => ({ file: f }));

      queuePrivateUploads({
        boardToUpload: board,
        emptyFiles,
        filesInFolders: [],
        filesWithNoExtension,
        rejectedFiles: [],
        topLevelFilesToUpload,
      });
    })();
  }, [
    createNewBoard,
    currentWorkspace?.id,
    generativeCanvasEnabled,
    goToBoardPage,
    queuePrivateUploads,
    router.isReady,
    router.query,
    validateUpload,
  ]);
};
